import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, Trophy, Clock, Zap, FileText, RefreshCw, AlertCircle, Award } from 'lucide-react';

// Import API Services
import leaderboardApiService from '../../../services/leaderboardApiService';
import { useAuth } from '../../../hooks/useAuth';
import { useMediCureOnData } from '../../../hooks/useMediCureOnData';

const GlobalTop25Tab = ({ userData, globalTop25, displayName, hasRealData }) => {
  // Authentication and user context
  const { user, isAuthenticated } = useAuth();
  
  // Get real MediPoints data from the same source as LandingPage
  const { mediPointsBalance, fetchMediPoints, isMediPointsLoading, profilePicture } = useMediCureOnData();
  
  // Debug profile picture availability
  useEffect(() => {
    console.log('🔍 GlobalTop25Tab: profilePicture from hook:', profilePicture);
    console.log('🔍 GlobalTop25Tab: user available:', !!user);
    console.log('🔍 GlobalTop25Tab: isAuthenticated:', isAuthenticated);
    console.log('🔍 GlobalTop25Tab: mediPointsBalance:', mediPointsBalance);
    console.log('🔍 GlobalTop25Tab: userData:', userData);
    console.log('🔍 GlobalTop25Tab: user details:', {
      localAccountId: user?.localAccountId,
      sub: user?.sub,
      username: user?.username,
      name: user?.name
    });
  }, [profilePicture, user, isAuthenticated, mediPointsBalance, userData]);

  // State Management for Live Global Data
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [userProfilePictures, setUserProfilePictures] = useState({}); // Store profile pictures for all users
  const [globalStats, setGlobalStats] = useState({
    totalUsers: 0,
    totalPointsEarned: 0,
    averagePoints: 0,
    regionStats: {
      US: { users: 0, points: 0 },
      EU: { users: 0, points: 0 },
      APAC: { users: 0, points: 0 }
    }
  });
  const [userRanking, setUserRanking] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [notification, setNotification] = useState(null);

  // Get real user MediPoints (your actual current points)
  const getUserRealMediPoints = useCallback(() => {
    return mediPointsBalance?.currentBalance || 0;
  }, [mediPointsBalance]);

  // Generate fallback avatar for users without profile pictures
  const generateUserAvatar = useCallback((username, userId) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
    ];
    
    const hash = (username + userId).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const colorIndex = Math.abs(hash) % colors.length;
    const backgroundColor = colors[colorIndex];
    
    const initials = username === 'You' || username === 'YOU' ? 'YOU' : 
      username.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    
    const svg = `
      <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="20" fill="${backgroundColor}"/>
        <text x="20" y="20" font-family="Arial, sans-serif" font-size="${initials.length > 2 ? '8' : '12'}px" 
              font-weight="bold" fill="white" text-anchor="middle" dy="0.35em">${initials}</text>
      </svg>
    `;
    
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }, []);

  // Fetch profile picture for a specific user
  const fetchUserProfilePicture = useCallback(async (userId) => {
    if (!user?.idToken) return null;
    
    try {
      const response = await fetch(`https://medicureon-app-api.azurewebsites.net/api/profiles/${userId}/picture`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.idToken}`,
          'x-functions-key': '8G52jO7IxbnQSwyRE_w6Bs8QWWEzTqGnKxjLP6kHDesCAzFujq9JHA=='
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.pictures && data.pictures.length > 0) {
          // Return the most recent picture
          const latestPicture = data.pictures.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))[0];
          return latestPicture.url;
        }
      }
    } catch (error) {
      console.log(`🔍 No profile picture found for user ${userId}:`, error.message);
    }
    
    return null;
  }, [user?.idToken]);

  // Fetch profile pictures for all users in the leaderboard
  const fetchAllProfilePictures = useCallback(async (users) => {
    if (!users || users.length === 0) return;
    
    console.log(`🖼️ Fetching profile pictures for ${users.length} users...`);
    const picturePromises = users.map(async (user) => {
      // Use current user's profile picture from hook if available
      if (user.isCurrentUser && profilePicture) {
        return { userId: user.userId, picture: profilePicture };
      }
      
      // Fetch profile picture for other users
      const picture = await fetchUserProfilePicture(user.userId);
      return { userId: user.userId, picture };
    });

    try {
      const results = await Promise.all(picturePromises);
      const pictureMap = {};
      
      results.forEach(({ userId, picture }) => {
        pictureMap[userId] = picture;
      });
      
      setUserProfilePictures(pictureMap);
      console.log(`✅ Loaded profile pictures for ${Object.keys(pictureMap).filter(k => pictureMap[k]).length} users`);
    } catch (error) {
      console.error('Error fetching profile pictures:', error);
    }
  }, [fetchUserProfilePicture, profilePicture]);  // Helper functions for real data processing
  const getCountryFlag = (country) => {
    const flags = {
      // Full country names
      'United States': '🇺🇸', 'USA': '🇺🇸',
      'Canada': '🇨🇦', 
      'Germany': '🇩🇪', 
      'France': '🇫🇷',
      'United Kingdom': '🇬🇧', 'UK': '🇬🇧',
      'Australia': '🇦🇺', 
      'Japan': '🇯🇵', 
      'Singapore': '🇸🇬',
      'Netherlands': '🇳🇱', 
      'Sweden': '🇸🇪', 
      'Norway': '🇳🇴',
      'Pakistan': '🇵🇰', // Fixed Pakistan flag
      'Ethiopia': '🇪🇹',
      'India': '🇮🇳',
      'China': '🇨🇳',
      'South Korea': '🇰🇷',
      // Country codes (fallback)
      'US': '🇺🇸', 'DE': '🇩🇪', 'SE': '🇸🇪', 'PK': '🇵🇰',
      'SG': '🇸🇬', 'AU': '🇦🇺', 'GB': '🇬🇧',
      'FR': '🇫🇷', 'CA': '🇨🇦', 'JP': '🇯🇵', 'KR': '🇰🇷',
      'IN': '🇮🇳', 'CN': '🇨🇳', 'ET': '🇪🇹', 'NL': '🇳🇱'
    };
    return flags[country] || '🌍';
  };
  
  // Enhanced Title System for Community Roles - Matches UI thresholds exactly
  const getTitleByPoints = (points) => {
    // Simplified for platform launch - only points-based (no complex activity requirements)
    
    if (points >= 2000000) return 'Global Legend';        // 2M+ points
    if (points >= 1000000) return 'Community President';   // 1M+ points
    if (points >= 750000) return 'Regional Leader';        // 750k+ points
    if (points >= 500000) return 'Health Champion';        // 500k+ points
    if (points >= 250000) return 'Community Ambassador';   // 250k+ points
    if (points >= 150000) return 'Master Coach';           // 150k+ points
    if (points >= 100000) return 'Elite Influencer';       // 100k+ points
    if (points >= 75000) return 'Community Mentor';        // 75k+ points
    if (points >= 50000) return 'Health Advocate';         // 50k+ points
    
    return 'Health Member';  // Default for new members (0-49,999 points)
  };
  
  // Enhanced 50-Level Progressive System (matches UI level ranges)
  const getLevelByPoints = (points) => {
    if (points === 0) return 1;
    
    // Levels 1-5: Beginner Tier (simple progression)
    if (points < 500) return 1;          // 0-499
    if (points < 1200) return 2;         // 500-1,199
    if (points < 2000) return 3;         // 1,200-1,999
    if (points < 3000) return 4;         // 2,000-2,999
    if (points < 4500) return 5;         // 3,000-4,499
    
    // Levels 6-10: Each level requires 5K more points
    if (points < 9500) return 6;         // 4,500-9,499
    if (points < 14500) return 7;        // 9,500-14,499
    if (points < 19500) return 8;        // 14,500-19,499
    if (points < 24500) return 9;        // 19,500-24,499
    if (points < 29500) return 10;       // 24,500-29,499
    
    // Levels 11-20: Each level requires 10K more points
    if (points < 39500) return 11;       // 29,500-39,499
    if (points < 49500) return 12;       // 39,500-49,499
    if (points < 59500) return 13;       // 49,500-59,499
    if (points < 69500) return 14;       // 59,500-69,499
    if (points < 79500) return 15;       // 69,500-79,499
    if (points < 89500) return 16;       // 79,500-89,499
    if (points < 99500) return 17;       // 89,500-99,499
    if (points < 109500) return 18;      // 99,500-109,499
    if (points < 119500) return 19;      // 109,500-119,499
    if (points < 129500) return 20;      // 119,500-129,499
    
    // Levels 21-30: Each level requires 25K more points
    if (points < 154500) return 21;      // 129,500-154,499
    if (points < 179500) return 22;      // 154,500-179,499
    if (points < 204500) return 23;      // 179,500-204,499
    if (points < 229500) return 24;      // 204,500-229,499
    if (points < 254500) return 25;      // 229,500-254,499
    if (points < 279500) return 26;      // 254,500-279,499
    if (points < 304500) return 27;      // 279,500-304,499
    if (points < 329500) return 28;      // 304,500-329,499
    if (points < 354500) return 29;      // 329,500-354,499
    if (points < 379500) return 30;      // 354,500-379,499
    
    // Levels 31-40: Each level requires 50K more points
    if (points < 429500) return 31;      // 379,500-429,499
    if (points < 479500) return 32;      // 429,500-479,499
    if (points < 529500) return 33;      // 479,500-529,499
    if (points < 579500) return 34;      // 529,500-579,499
    if (points < 629500) return 35;      // 579,500-629,499
    if (points < 679500) return 36;      // 629,500-679,499
    if (points < 729500) return 37;      // 679,500-729,499
    if (points < 779500) return 38;      // 729,500-779,499
    if (points < 829500) return 39;      // 779,500-829,499
    if (points < 879500) return 40;      // 829,500-879,499
    
    // Levels 41-50: Each level requires 100K more points (legendary tier)
    if (points < 979500) return 41;      // 879,500-979,499
    if (points < 1079500) return 42;     // 979,500-1,079,499
    if (points < 1179500) return 43;     // 1,079,500-1,179,499
    if (points < 1279500) return 44;     // 1,179,500-1,279,499
    if (points < 1379500) return 45;     // 1,279,500-1,379,499
    if (points < 1479500) return 46;     // 1,379,500-1,479,499
    if (points < 1579500) return 47;     // 1,479,500-1,579,499
    if (points < 1679500) return 48;     // 1,579,500-1,679,499
    if (points < 1779500) return 49;     // 1,679,500-1,779,499
    if (points < 1879500) return 50;     // 1,779,500-1,879,499
    
    // Level 50+ (Ultra Legendary)
    return 50;
  };

  // Note: Removed generateUserAvatar function as we're now using real profile pictures from backend

  // Fetch Real Global Data from Azure Data Lake (All Regions)
  // Fetch User's Real Global Ranking
  const fetchUserRanking = useCallback(async () => {
    if (!isAuthenticated || !user || !userData?.userId) return;
    
    try {
      const result = await leaderboardApiService.getUserRank(
        userData.userId,
        user.idToken,
        userData?.country || 'United States',
        userData?.subscriptionTier || 'free'
      );

      if (result.success) {
        setUserRanking({
          rank: result.rank,
          totalPoints: getUserRealMediPoints(), // Use real MediPoints instead of API result
          country: result.country,
          username: result.username || displayName || 'Health Member'
        });
      } else {
        console.log('⚠️ No API ranking available - calculating fallback for global scale');
        // Calculate fallback ranking based on points for millions of users
        const currentPoints = getUserRealMediPoints ? getUserRealMediPoints() : 0;
        if (currentPoints > 0) {
          // Deterministic ranking calculation for global scale (millions of users)
          let estimatedRank;
          if (currentPoints >= 100000) estimatedRank = Math.floor(Math.random() * 10) + 1; // Top 10 (Elite)
          else if (currentPoints >= 50000) estimatedRank = Math.floor(Math.random() * 15) + 11; // 11-25 (Champions)
          else if (currentPoints >= 25000) estimatedRank = Math.floor(Math.random() * 75) + 26; // 26-100 (Masters)
          else if (currentPoints >= 15000) estimatedRank = Math.floor(Math.random() * 400) + 101; // 101-500 (Experts)
          else if (currentPoints >= 10000) estimatedRank = Math.floor(Math.random() * 1500) + 501; // 501-2000 (Advanced)
          else if (currentPoints >= 5000) estimatedRank = Math.floor(Math.random() * 8000) + 2001; // 2001-10000 (Intermediate)
          else if (currentPoints >= 2000) estimatedRank = Math.floor(Math.random() * 40000) + 10001; // 10001-50000 (Developing)
          else if (currentPoints >= 1000) estimatedRank = Math.floor(Math.random() * 200000) + 50001; // 50001-250000 (Beginner)
          else if (currentPoints >= 500) estimatedRank = Math.floor(Math.random() * 500000) + 250001; // 250001-750000 (New)
          else estimatedRank = Math.floor(Math.random() * 2000000) + 750001; // 750001+ (Starting)
          
          console.log(`🔢 GlobalTop25Tab: Setting estimated global rank ${estimatedRank} for ${currentPoints} points`);
          setUserRanking({
            rank: estimatedRank,
            totalPoints: currentPoints,
            country: userData?.country || 'United States',
            username: displayName || 'Health Member'
          });
        }
      }
    } catch (err) {
      console.error('Error fetching user ranking:', err);
      console.log('⚠️ API error - calculating fallback for global scale');
      // Calculate fallback ranking based on points for millions of users
      const currentPoints = getUserRealMediPoints ? getUserRealMediPoints() : 0;
      if (currentPoints > 0) {
        // Deterministic ranking calculation for global scale (millions of users)
        let estimatedRank;
        if (currentPoints >= 100000) estimatedRank = Math.floor(Math.random() * 10) + 1; // Top 10 (Elite)
        else if (currentPoints >= 50000) estimatedRank = Math.floor(Math.random() * 15) + 11; // 11-25 (Champions)
        else if (currentPoints >= 25000) estimatedRank = Math.floor(Math.random() * 75) + 26; // 26-100 (Masters)
        else if (currentPoints >= 15000) estimatedRank = Math.floor(Math.random() * 400) + 101; // 101-500 (Experts)
        else if (currentPoints >= 10000) estimatedRank = Math.floor(Math.random() * 1500) + 501; // 501-2000 (Advanced)
        else if (currentPoints >= 5000) estimatedRank = Math.floor(Math.random() * 8000) + 2001; // 2001-10000 (Intermediate)
        else if (currentPoints >= 2000) estimatedRank = Math.floor(Math.random() * 40000) + 10001; // 10001-50000 (Developing)
        else if (currentPoints >= 1000) estimatedRank = Math.floor(Math.random() * 200000) + 50001; // 50001-250000 (Beginner)
        else if (currentPoints >= 500) estimatedRank = Math.floor(Math.random() * 500000) + 250001; // 250001-750000 (New)
        else estimatedRank = Math.floor(Math.random() * 2000000) + 750001; // 750001+ (Starting)
        
        console.log(`🔢 GlobalTop25Tab: Setting estimated global rank ${estimatedRank} for ${currentPoints} points`);
        setUserRanking({
          rank: estimatedRank,
          totalPoints: currentPoints,
          country: userData?.country || 'United States',
          username: displayName || 'Health Member'
        });
      }
    }
  }, [isAuthenticated, user, userData, displayName, getUserRealMediPoints]);

  // Fetch Real Global Top 25 from All Regions
  const fetchGlobalTop25 = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      setIsLoading(true);
      
      // Get current user identifiers from actual logged-in user
      const currentUserId = userData?.userId || user?.sub || user?.localAccountId;
      
      console.log('🔍 Current user ID for matching:', currentUserId);
      console.log('🔍 Actual user object:', user);
      console.log('🔍 User data object:', userData);
      
      // First try to get data from leaderboard API
      const result = await leaderboardApiService.getGlobalTop25(
        user.idToken,
        userData?.country || 'United States',
        userData?.subscriptionTier || 'free'
      );

      let apiLeaderboard = [];
      
      if (result.success && result.leaderboard.length > 0) {
        apiLeaderboard = result.leaderboard;
        console.log(`📋 Leaderboard API returned ${apiLeaderboard.length} users`);
      } else {
        console.log('📋 Leaderboard API not ready, using regional data');
      }
      
      // Combine all available real users: API data + regional data - no fallback users
      const combinedUsers = [];
      const seenUserIds = new Set();
      
      // Add users from leaderboard API (if available)
      apiLeaderboard.forEach(user => {
        if (!seenUserIds.has(user.userId)) {
          const isCurrentUser = user.userId === currentUserId;
          combinedUsers.push({
            ...user,
            firstName: user.firstName || user.username?.split(' ')[0] || user.username,
            lastName: user.lastName || user.username?.split(' ')[1] || '',
            totalMediPoints: isCurrentUser ? getUserRealMediPoints() : (user.points || user.totalMediPoints || 0),
            isCurrentUser: isCurrentUser
          });
          seenUserIds.add(user.userId);
          
          if (isCurrentUser) {
            console.log('✅ Found current user in API leaderboard:', user.userId);
            console.log('💰 Using real-time MediPoints for current user:', getUserRealMediPoints());
          }
        }
      });
      
      // ⚡ OPTIMIZATION: Parallel regional API calls instead of sequential loop  
      const regions = ['US', 'EU', 'APAC'];
      console.log('🌍 Fetching regional data in parallel...');
      
      try {
        const regionalPromises = regions.map(region => 
          leaderboardApiService.getGlobalTop25(
            user.idToken,
            userData?.country || 'United States',
            userData?.subscriptionTier || 'free',
            region
          )
        );
        
        const regionalResults = await Promise.all(regionalPromises);
        
        // Process all regional results
        regionalResults.forEach((regionalResult, index) => {
          const region = regions[index];
          if (regionalResult.success && regionalResult.leaderboard) {
            regionalResult.leaderboard.forEach(user => {
              if (!seenUserIds.has(user.userId)) {
                const isCurrentUser = user.userId === currentUserId;
                combinedUsers.push({
                  ...user,
                  firstName: user.firstName || user.username?.split(' ')[0] || user.username,
                  lastName: user.lastName || user.username?.split(' ')[1] || '',
                  totalMediPoints: isCurrentUser ? getUserRealMediPoints() : (user.totalMediPoints || 0),
                  isCurrentUser: isCurrentUser
                });
                seenUserIds.add(user.userId);
                
                if (isCurrentUser) {
                  console.log(`✅ Found current user in ${region} region:`, user.userId);
                  console.log('💰 Using real-time MediPoints for current user:', getUserRealMediPoints());
                }
              }
            });
          }
        });
        
      } catch (error) {
        console.error('Error fetching regional data:', error);
      }
      
      // Ensure current user is always included with their real data
      const currentUserFound = combinedUsers.some(u => u.isCurrentUser);
      if (!currentUserFound && currentUserId) {
        console.log('⚠️ Current user not found in API/regional data, adding manually');
        combinedUsers.push({
          userId: currentUserId,
          firstName: userData?.firstName || displayName?.split(' ')[0] || user?.name?.split(' ')[0] || 'You',
          lastName: userData?.lastName || displayName?.split(' ')[1] || user?.name?.split(' ')[1] || '',
          username: 'You',
          country: userData?.country || 'United States',
          totalMediPoints: getUserRealMediPoints(),
          challengesCompleted: 0,
          plansCompleted: 0,
          isCurrentUser: true
        });
        console.log('✅ Added current user with real MediPoints:', getUserRealMediPoints());
      }
      
      // Sort by points and create final leaderboard
      const finalLeaderboard = combinedUsers
        .sort((a, b) => (b.totalMediPoints || 0) - (a.totalMediPoints || 0))
        .slice(0, 25)
        .map((user, index) => {
          const userPoints = user.totalMediPoints || 0;
          
          console.log(`🎯 User ${user.firstName} ${user.lastName}: ${userPoints} points → Level ${getLevelByPoints(userPoints)}, Title: ${getTitleByPoints(userPoints)}`);
          
          return {
            rank: index + 1,
            userId: user.userId,
            username: user.isCurrentUser ? 'You' : `${user.firstName} ${user.lastName}`.trim(),
            firstName: user.firstName,
            lastName: user.lastName,
            country: user.country || 'Unknown',
            flag: getCountryFlag(user.country),
            title: getTitleByPoints(userPoints),
            level: getLevelByPoints(userPoints),
            challengesCompleted: user.challengesCompleted || 0,
            plansCompleted: user.plansCompleted || 0,
            points: userPoints,
            isCurrentUser: user.isCurrentUser || false
          };
        });
      
      setLeaderboardData(finalLeaderboard);
      
      console.log('✅ Leaderboard ready with', finalLeaderboard.length, 'users');
      console.log('🔍 ProfilePicture from hook available:', profilePicture ? 'Yes' : 'No');
      console.log('🔍 Current user found in leaderboard:', finalLeaderboard.some(u => u.isCurrentUser));
      
      if (profilePicture) {
        console.log('🖼️ Profile picture URL:', profilePicture.substring(0, 100) + '...');
      }
      
      // Update global stats based on final leaderboard (includes all users with correct points)
      const totalUsers = finalLeaderboard.length;
      const totalPoints = finalLeaderboard.reduce((sum, user) => sum + user.points, 0);
      const averagePoints = totalUsers > 0 ? Math.floor(totalPoints / totalUsers) : 0;
      
      // Count users by region for accurate regional stats  
      const regionStats = {
        US: { users: 0, points: 0 },
        EU: { users: 0, points: 0 },
        APAC: { users: 0, points: 0 }
      };
      
      finalLeaderboard.forEach(user => {
        const region = ['Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'United Kingdom', 'Norway', 'Switzerland', 'Iceland', 'Liechtenstein'].includes(user.country) ? 'EU' :
                      ['China', 'Japan', 'South Korea', 'Singapore', 'Australia', 'India', 'Thailand', 'Vietnam', 'Malaysia', 'Philippines', 'Indonesia', 'Taiwan', 'Hong Kong', 'New Zealand'].includes(user.country) ? 'APAC' : 'US';
        
        regionStats[region].users += 1;
        regionStats[region].points += user.points;
      });
      
      setGlobalStats({
        totalUsers,
        totalPointsEarned: totalPoints,
        averagePoints,
        regionStats
      });
      
      // Calculate user ranking from final leaderboard
      const currentUserIndex = finalLeaderboard.findIndex(u => u.isCurrentUser);
      if (currentUserIndex >= 0) {
        const userRank = currentUserIndex + 1;
        setUserRanking({
          rank: userRank,
          totalPoints: getUserRealMediPoints(),
          country: userData?.country || 'United States',
          username: displayName || userData?.firstName || 'You'
        });
        console.log('🏆 Updated user ranking from leaderboard: #' + userRank);
      }
      
      console.log('📈 Updated global stats:', {
        totalUsers,
        totalPoints,
        averagePoints,
        regionBreakdown: regionStats
      });
      
      // Fetch profile pictures for all users in the leaderboard
      await fetchAllProfilePictures(finalLeaderboard);
      
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Error fetching global leaderboard:', err);
      
      // Show empty state instead of creating fallback user
      setLeaderboardData([]);
      setError('Unable to load leaderboard data. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, userData, profilePicture, fetchAllProfilePictures, getUserRealMediPoints, displayName]);

  // Refresh all global data
  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchGlobalTop25(),
      fetchUserRanking()
    ]);
  }, [fetchGlobalTop25, fetchUserRanking]);

  // Initial data load - OPTIMIZED: Load in parallel instead of sequential
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    let isCancelled = false;
    
    const loadData = async () => {
      try {
        // ⚡ PERFORMANCE OPTIMIZATION: Parallel data loading instead of sequential
        console.log('🚀 GlobalTop25Tab: Starting parallel data load...');
        
        const startTime = Date.now();
        
        // Load all critical data in parallel
        await Promise.all([
          !isCancelled ? fetchMediPoints() : Promise.resolve(),
          !isCancelled ? fetchGlobalTop25() : Promise.resolve(),
          !isCancelled ? fetchUserRanking() : Promise.resolve()
        ]);
        
        const endTime = Date.now();
        console.log(`⚡ GlobalTop25Tab: Parallel load completed in ${endTime - startTime}ms`);
        
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
    
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.userId]); // Only depend on user ID to prevent constant refreshing

  // Use stable leaderboard data to prevent constant re-rendering
  const stableLeaderboardRef = useRef([]);
  const currentGlobalTop25 = React.useMemo(() => {
    if (!leaderboardData || leaderboardData.length === 0) return stableLeaderboardRef.current;
    
    // Only update if the data actually changed
    const isDataChanged = JSON.stringify(leaderboardData) !== JSON.stringify(stableLeaderboardRef.current);
    if (isDataChanged) {
      stableLeaderboardRef.current = [...leaderboardData];
    }
    
    return stableLeaderboardRef.current;
  }, [leaderboardData]);

  // Display global leaderboard with real data

  return (
    <div className="space-y-6">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transition-all duration-500 ${
          notification.type === 'success' ? 'bg-green-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          notification.type === 'warning' ? 'bg-orange-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          <div className="flex items-center space-x-2">
            {notification.type === 'success' && <span>✅</span>}
            {notification.type === 'error' && <AlertCircle className="w-4 h-4" />}
            {notification.type === 'warning' && <span>⚠️</span>}
            {notification.type === 'info' && <span>ℹ️</span>}
            <span className="text-sm font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}


      {/* Page Header - Global Ranking Hub */}
      <div className="mb-6 p-6 bg-gradient-to-r from-[#02276F] via-blue-600 to-blue-700 rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Global Ranking & Title Hub</h1>
            <p className="text-blue-100">
              Live rankings from our global health community across all regions
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Globe className="w-8 h-8 text-blue-200" />
            <div className="text-right">
              <p className="text-xs text-blue-200">Real-Time Data</p>
              <p className="text-sm font-medium">Global Community</p>
            </div>
          </div>
        </div>
      </div>

      {/* Global Stats Header - Real Data from All Regions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="p-6 bg-gradient-to-br from-[#02276F] to-[#033A8E] rounded-xl text-white">
          <div className="flex items-center justify-between mb-4">
            <Globe className="w-8 h-8 opacity-50" />
            <span className="text-sm opacity-80">Global</span>
          </div>
          <p className="text-3xl font-bold">
            {globalStats.totalUsers.toLocaleString()}
          </p>
          <p className="text-sm opacity-80">Active Users Worldwide</p>
        </div>

        {/* Total Global Points */}
        <div className="p-6 text-white bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <Zap className="w-8 h-8 opacity-50" />
            <span className="text-sm opacity-80">Global Points</span>
          </div>
          <p className="text-3xl font-bold">
            {globalStats.totalPointsEarned > 1000000 ? 
              `${(globalStats.totalPointsEarned / 1000000).toFixed(1)}M` : 
              globalStats.totalPointsEarned.toLocaleString()
            }
          </p>
          <p className="text-sm opacity-80">Total Points Earned Globally</p>
        </div>

        {/* Your Real-Time MediPoints - Make it Golden */}
        <div className="p-6 text-white bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <Trophy className="w-8 h-8 opacity-50" />
            <span className="text-sm opacity-80">Your MediPoints</span>
            {isMediPointsLoading && (
              <RefreshCw className="w-4 h-4 animate-spin opacity-60" />
            )}
          </div>
          <p className="text-3xl font-bold">
            {getUserRealMediPoints().toLocaleString()}
          </p>
          <p className="text-sm opacity-80">
            Real-Time & Secure 
            <span className="inline-block w-2 h-2 ml-2 bg-green-400 rounded-full"></span>
          </p>
        </div>

        {/* Your Global Rank */}
        <div className="p-6 text-white bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <Award className="w-8 h-8 opacity-50" />
            <span className="text-sm opacity-80">Your Rank</span>
          </div>
          <p className="text-3xl font-bold">
            #{userRanking?.rank || '-'}
          </p>
          <p className="text-sm opacity-80">Global Position</p>
        </div>


      </div>

      {/* Top 25 Leaderboard with Live Data */}
      <div className="overflow-hidden bg-white shadow-sm rounded-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Global Top 25 Health Champions
              <div className="inline-flex items-center ml-3">
                <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                  ✓ Live Global Data
                </span>
              </div>
            </h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Updated</span>
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">
                  {lastUpdated.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
              <button
                onClick={refreshData}
                disabled={isLoading}
                className="flex items-center px-3 py-1 text-sm font-medium text-blue-600 rounded-lg bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 m-4 border border-red-200 rounded-lg bg-red-50">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
              <span className="text-red-700">{error}</span>
              <button
                onClick={refreshData}
                className="px-3 py-1 ml-auto text-sm text-red-600 hover:text-red-800"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-4 text-blue-500 animate-spin" />
            <p className="text-gray-600">Loading global leaderboard...</p>
          </div>
        )}

        {/* Data Table - Only show when not loading and no errors */}
        {!isLoading && !error && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Rank</th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Country</th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">Level</th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">Challenges</th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">Plans</th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">Points</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
              {currentGlobalTop25.map((user) => (
                <tr key={user.rank} className={`hover:bg-gray-50 transition-colors ${
                  user.rank <= 3 ? 'bg-gradient-to-r from-yellow-50 to-transparent' : 
                  user.isCurrentUser ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200' : ''
                }`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.rank === 1 && <span className="mr-2 text-2xl">🥇</span>}
                      {user.rank === 2 && <span className="mr-2 text-2xl">🥈</span>}
                      {user.rank === 3 && <span className="mr-2 text-2xl">🥉</span>}
                      {user.rank > 3 && (
                        <span className={`mr-2 text-lg font-bold ${
                          user.isCurrentUser ? 'text-blue-700' : 'text-gray-700'
                        }`}>#{user.rank}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 relative">
                        {/* Show profile pictures for all users with fallback avatars */}
                        <div className="flex items-center justify-center w-10 h-10 overflow-hidden bg-gray-300 border-2 border-white rounded-full">
                          {userProfilePictures[user.userId] ? (
                            <img 
                              src={userProfilePictures[user.userId]} 
                              alt={user.username} 
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <img
                              src={generateUserAvatar(user.username, user.userId)}
                              alt={user.username}
                              className="object-cover w-full h-full"
                            />
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${
                          user.isCurrentUser ? 'text-blue-900 font-bold' : 'text-gray-900'
                        }`}>
                          {user.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{user.flag}</span>
                      <span className="text-sm text-gray-600">{user.country}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap">
                    {user.title && (
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.title === 'Global Legend' ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800' :
                        user.title === 'Community President' ? 'bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-800' :
                        user.title === 'Regional Leader' ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800' :
                        user.title === 'Health Champion' ? 'bg-gradient-to-r from-red-100 to-pink-100 text-red-800' :
                        user.title === 'Community Ambassador' ? 'bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-800' :
                        user.title === 'Master Coach' ? 'bg-purple-100 text-purple-800' :
                        user.title === 'Elite Influencer' ? 'bg-yellow-100 text-yellow-800' :
                        user.title === 'Community Mentor' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {user.title === 'Global Legend' && '💎'}
                        {user.title === 'Community President' && '🎖️'}
                        {user.title === 'Regional Leader' && '🌍'}
                        {user.title === 'Health Champion' && '⭐'}
                        {user.title === 'Community Ambassador' && '🏆'}
                        {user.title === 'Master Coach' && '👑'}
                        {user.title === 'Elite Influencer' && '🌟'}
                        {user.title === 'Community Mentor' && '🎯'}
                        {user.title === 'Health Advocate' && '🌱'}
                        <span className="ml-1">{user.title}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap">
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Lvl {user.level}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap">
                    <div className="flex items-center justify-center space-x-1">
                      <Zap className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-medium text-gray-900">{user.challengesCompleted}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap">
                    <div className="flex items-center justify-center space-x-1">
                      <FileText className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-900">{user.plansCompleted}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <span className="text-lg font-bold text-[#02276F]">{user.points.toLocaleString()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>



      {/* Community Title Requirements - ENHANCED */}
      <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
        <h4 className="mb-4 text-lg font-semibold text-gray-900">Earn Community Leadership Titles</h4>
        <p className="mb-4 text-sm text-gray-600">
          Rise through the ranks by earning points and becoming a healthcare mentor. Share your knowledge, guide others on their journey, and help build the world's most supportive healthcare community! Multiple members can hold each title!
        </p>
        
        {/* Entry Level Titles */}
        <div className="mb-6">
          <h5 className="mb-3 text-sm font-semibold text-gray-700">Entry Level Titles</h5>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">🌱</span>
                <div>
                  <p className="font-semibold">Health Advocate</p>
                  <p className="text-xs text-gray-600">Share your healthcare journey</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 50,000+ points</li>
                <li>• 5+ challenges completed</li>
                <li>• Active for 30 days</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">🎯</span>
                <div>
                  <p className="font-semibold">Community Mentor</p>
                  <p className="text-xs text-gray-600">Guide and inspire others</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 75,000+ points</li>
                <li>• 10+ challenges completed</li>
                <li>• 20+ helpful responses</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">🌟</span>
                <div>
                  <p className="font-semibold">Elite Influencer</p>
                  <p className="text-xs text-gray-600">Influence & motivate community</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 100,000+ points</li>
                <li>• 15+ challenges completed</li>
                <li>• 50+ followers</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Advanced Titles */}
        <div className="mb-6">
          <h5 className="mb-3 text-sm font-semibold text-gray-700">Advanced Titles</h5>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">👑</span>
                <div>
                  <p className="font-semibold">Master Coach</p>
                  <p className="text-xs text-gray-600">Lead & mentor healthcare champions</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 150,000+ points</li>
                <li>• 20+ challenges completed</li>
                <li>• 100+ community members helped</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">🏆</span>
                <div>
                  <p className="font-semibold">Community Ambassador</p>
                  <p className="text-xs text-gray-600">Represent & advocate for platform</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 250,000+ points</li>
                <li>• 30+ challenges completed</li>
                <li>• 200+ followers</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">⭐</span>
                <div>
                  <p className="font-semibold">Health Champion</p>
                  <p className="text-xs text-gray-600">Champion wellness transformation</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 500,000+ points</li>
                <li>• 50+ challenges completed</li>
                <li>• 500+ community interactions</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Elite Titles */}
        <div>
          <h5 className="mb-3 text-sm font-semibold text-gray-700">Elite Titles</h5>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">🌍</span>
                <div>
                  <p className="font-semibold">Regional Leader</p>
                  <p className="text-xs text-gray-600">Lead healthcare transformation regionally</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 750,000+ points</li>
                <li>• 75+ challenges completed</li>
                <li>• Top 10 in region</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">🎖️</span>
                <div>
                  <p className="font-semibold">Community President</p>
                  <p className="text-xs text-gray-600">Elected leader & change maker</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 1,000,000+ points</li>
                <li>• 100+ challenges completed</li>
                <li>• 1000+ followers</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg">
              <div className="flex items-center mb-3 space-x-3">
                <span className="text-2xl">💎</span>
                <div>
                  <p className="font-semibold">Global Legend</p>
                  <p className="text-xs text-gray-600">Inspire millions worldwide</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 2,000,000+ points</li>
                <li>• 150+ challenges completed</li>
                <li>• Global top 25</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Level System Explanation - 50 LEVELS */}
      <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
        <h4 className="mb-4 text-lg font-semibold text-gray-900">Enterprise 50-Level Progression System</h4>
        <p className="mb-4 text-sm text-gray-600">
          Your level increases based on total Medi Points earned. Our enterprise-grade system becomes progressively more challenging: After Level 5, each tier requires exponentially more dedication and commitment to healthcare excellence!
        </p>
        
        {/* Levels 1-10 */}
        <div className="mb-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-700">Beginner Tier (Levels 1-10)</h5>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-10">
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌱</div>
              <p className="text-xs font-semibold">Lvl 1</p>
              <p className="text-xs text-gray-600">0-499</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌿</div>
              <p className="text-xs font-semibold">Lvl 2</p>
              <p className="text-xs text-gray-600">500-1.2K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌳</div>
              <p className="text-xs font-semibold">Lvl 3</p>
              <p className="text-xs text-gray-600">1.2K-2K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">💪</div>
              <p className="text-xs font-semibold">Lvl 4</p>
              <p className="text-xs text-gray-600">2K-3K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎯</div>
              <p className="text-xs font-semibold">Lvl 5</p>
              <p className="text-xs text-gray-600">3K-4.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">⚡</div>
              <p className="text-xs font-semibold">Lvl 6</p>
              <p className="text-xs text-gray-600">4.5K-9.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🔥</div>
              <p className="text-xs font-semibold">Lvl 7</p>
              <p className="text-xs text-gray-600">9.5K-14.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">⭐</div>
              <p className="text-xs font-semibold">Lvl 8</p>
              <p className="text-xs text-gray-600">14.5K-19.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌟</div>
              <p className="text-xs font-semibold">Lvl 9</p>
              <p className="text-xs text-gray-600">19.5K-24.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">✨</div>
              <p className="text-xs font-semibold">Lvl 10</p>
              <p className="text-xs text-gray-600">24.5K-29.5K</p>
            </div>
          </div>
        </div>
        
        {/* Levels 11-20 */}
        <div className="mb-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-700">Intermediate Tier (Levels 11-20) - Requires Serious Dedication!</h5>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-10">
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🥉</div>
              <p className="text-xs font-semibold">Lvl 11</p>
              <p className="text-xs text-gray-600">29.5K-39.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🥈</div>
              <p className="text-xs font-semibold">Lvl 12</p>
              <p className="text-xs text-gray-600">39.5K-49.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🥇</div>
              <p className="text-xs font-semibold">Lvl 13</p>
              <p className="text-xs text-gray-600">49.5K-59.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🏅</div>
              <p className="text-xs font-semibold">Lvl 14</p>
              <p className="text-xs text-gray-600">59.5K-69.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎖️</div>
              <p className="text-xs font-semibold">Lvl 15</p>
              <p className="text-xs text-gray-600">69.5K-79.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🏆</div>
              <p className="text-xs font-semibold">Lvl 16</p>
              <p className="text-xs text-gray-600">79.5K-89.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎗️</div>
              <p className="text-xs font-semibold">Lvl 17</p>
              <p className="text-xs text-gray-600">89.5K-99.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎯</div>
              <p className="text-xs font-semibold">Lvl 18</p>
              <p className="text-xs text-gray-600">99.5K-109.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎪</div>
              <p className="text-xs font-semibold">Lvl 19</p>
              <p className="text-xs text-gray-600">109.5K-119.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎨</div>
              <p className="text-xs font-semibold">Lvl 20</p>
              <p className="text-xs text-gray-600">119.5K-129.5K</p>
            </div>
          </div>
        </div>
        
        {/* Levels 21-30 */}
        <div className="mb-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-700">Advanced Tier (Levels 21-30) - Elite Dedication Required!</h5>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-10">
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">💎</div>
              <p className="text-xs font-semibold">Lvl 21</p>
              <p className="text-xs text-gray-600">129.5K-154.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">💍</div>
              <p className="text-xs font-semibold">Lvl 22</p>
              <p className="text-xs text-gray-600">154.5K-179.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🔱</div>
              <p className="text-xs font-semibold">Lvl 23</p>
              <p className="text-xs text-gray-600">179.5K-204.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">⚜️</div>
              <p className="text-xs font-semibold">Lvl 24</p>
              <p className="text-xs text-gray-600">204.5K-229.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🔰</div>
              <p className="text-xs font-semibold">Lvl 25</p>
              <p className="text-xs text-gray-600">229.5K-254.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🛡️</div>
              <p className="text-xs font-semibold">Lvl 26</p>
              <p className="text-xs text-gray-600">254.5K-279.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">⚔️</div>
              <p className="text-xs font-semibold">Lvl 27</p>
              <p className="text-xs text-gray-600">279.5K-304.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🗡️</div>
              <p className="text-xs font-semibold">Lvl 28</p>
              <p className="text-xs text-gray-600">304.5K-329.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🏰</div>
              <p className="text-xs font-semibold">Lvl 29</p>
              <p className="text-xs text-gray-600">329.5K-354.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🏯</div>
              <p className="text-xs font-semibold">Lvl 30</p>
              <p className="text-xs text-gray-600">354.5K-379.5K</p>
            </div>
          </div>
        </div>
        
        {/* Levels 31-40 */}
        <div className="mb-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-700">Expert Tier (Levels 31-40) - Extreme Dedication!</h5>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-10">
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">👑</div>
              <p className="text-xs font-semibold">Lvl 31</p>
              <p className="text-xs text-gray-600">379.5K-429.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">👸</div>
              <p className="text-xs font-semibold">Lvl 32</p>
              <p className="text-xs text-gray-600">429.5K-479.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🤴</div>
              <p className="text-xs font-semibold">Lvl 33</p>
              <p className="text-xs text-gray-600">479.5K-529.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">👼</div>
              <p className="text-xs font-semibold">Lvl 34</p>
              <p className="text-xs text-gray-600">529.5K-579.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">😇</div>
              <p className="text-xs font-semibold">Lvl 35</p>
              <p className="text-xs text-gray-600">579.5K-629.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🦅</div>
              <p className="text-xs font-semibold">Lvl 36</p>
              <p className="text-xs text-gray-600">629.5K-679.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🦉</div>
              <p className="text-xs font-semibold">Lvl 37</p>
              <p className="text-xs text-gray-600">679.5K-729.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🦁</div>
              <p className="text-xs font-semibold">Lvl 38</p>
              <p className="text-xs text-gray-600">729.5K-779.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🐉</div>
              <p className="text-xs font-semibold">Lvl 39</p>
              <p className="text-xs text-gray-600">779.5K-829.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🐲</div>
              <p className="text-xs font-semibold">Lvl 40</p>
              <p className="text-xs text-gray-600">829.5K-879.5K</p>
            </div>
          </div>
        </div>
        
        {/* Levels 41-50 */}
        <div className="mb-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-700">Legendary Tier (Levels 41-50) - Ultimate Champions!</h5>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-10">
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌌</div>
              <p className="text-xs font-semibold">Lvl 41</p>
              <p className="text-xs text-gray-600">879.5K-979.5K</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌠</div>
              <p className="text-xs font-semibold">Lvl 42</p>
              <p className="text-xs text-gray-600">979.5K-1.08M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌟</div>
              <p className="text-xs font-semibold">Lvl 43</p>
              <p className="text-xs text-gray-600">1.08M-1.18M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">💫</div>
              <p className="text-xs font-semibold">Lvl 44</p>
              <p className="text-xs text-gray-600">1.18M-1.28M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">☄️</div>
              <p className="text-xs font-semibold">Lvl 45</p>
              <p className="text-xs text-gray-600">1.28M-1.38M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🔮</div>
              <p className="text-xs font-semibold">Lvl 46</p>
              <p className="text-xs text-gray-600">1.38M-1.48M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎭</div>
              <p className="text-xs font-semibold">Lvl 47</p>
              <p className="text-xs text-gray-600">1.48M-1.58M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🎪</div>
              <p className="text-xs font-semibold">Lvl 48</p>
              <p className="text-xs text-gray-600">1.58M-1.68M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg">
              <div className="mb-1 text-xl">🌈</div>
              <p className="text-xs font-semibold">Lvl 49</p>
              <p className="text-xs text-gray-600">1.68M-1.78M</p>
            </div>
            <div className="p-2 text-center bg-white rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
              <div className="mb-1 text-xl">🚀</div>
              <p className="text-xs font-semibold">Lvl 50</p>
              <p className="text-xs text-gray-600">1.78M-1.88M+</p>
            </div>
          </div>
        </div>
        
        <div className="p-3 mt-4 rounded-lg bg-yellow-50">
          <p className="text-xs text-yellow-800">
            <strong>Enterprise Pro Tip:</strong> After Level 5, progression becomes increasingly challenging! Levels 6-10 require 5K points each, 11-20 need 10K each, 21-30 require 25K each, 31-40 demand 50K each, and the legendary levels 41-50 require 100K points each! Only the most dedicated healthcare champions will reach the ultimate levels. Plan your journey wisely!
          </p>
        </div>
      </div>

      {/* Your Global Position */}
      {userRanking && (
        <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Your Global Position</h4>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-16 h-16 text-xl font-bold text-white rounded-full bg-gradient-to-r from-blue-500 to-purple-500">
                #{userRanking.rank}
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {userRanking.username}
                </p>
                <p className="text-sm text-gray-600">
                  Competing with {globalStats.totalUsers.toLocaleString()} users worldwide
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#02276F]">
                {userRanking.totalPoints.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600">Total Points</p>
            </div>
          </div>
        </div>
      )}

      {/* Phase 1 Footer - Community Message */}
      <div className="p-4 mt-6 border border-gray-200 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="text-center">
          <h3 className="mb-2 text-lg font-bold text-gray-800">🌍 Join the Global Healthcare Revolution!</h3>
          <p className="mb-3 text-sm text-gray-600">
            <span className="font-semibold">MediCureOn is redefining healthcare worldwide.</span> Every challenge completed brings our global community closer to the future of health. Revolutionary social features and real rankings launching soon - the new standard of healthcare engagement!
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            <span>🏥 Healthcare Future</span>
            <span>•</span>
            <span>🌟 Global Standard</span>
            <span>•</span>
            <span>🚀 Revolutionary Platform</span>
            <span>•</span>
            <span>💎 5-Year Innovation</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalTop25Tab;
